import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { OptimizedBrick } from "../utils/brickOptimizer";
import "./BOMGenerator.css";

// Import modularized components
import BOMControls from "./BOMGenerator/BOMControls";
import BOMSummary from "./BOMGenerator/BOMSummary";
import BOMDetails from "./BOMGenerator/BOMDetails";
import BOMNotes from "./BOMGenerator/BOMNotes";

// Import custom hook
import { useBOMCalculation } from "./BOMGenerator/useBOMCalculation";

// Import CSV export helper
import {
  exportBomCsv,
  exportBomBrickLinkCsv,
  exportBomRebrickableCsv,
  type BomCsvRow,
} from "../utils/bomCsvExport";

interface BOMData {
  totalBricks: number;
  totalCost: number;
  brickInventory: Array<{
    brickType: any;
    colorIndex: number;
    colorName: string;
    quantity: number;
    estimatedPrice: number;
  }>;
  colorSummary: { [key: string]: number };
  layers: Array<{
    layer: number;
    brickCount: number;
    colorDistribution: { [key: string]: number };
  }>;
}

interface BOMGeneratorProps {
  layers: Array<{
    layer: number;
    bricks: OptimizedBrick[];
  }>;
  onExport: (bomData: BOMData) => void;
}

const BOMGenerator: React.FC<BOMGeneratorProps> = ({ layers, onExport }) => {
  const { t } = useTranslation();
  const [currency, setCurrency] = useState("USD");
  const [showCosts, setShowCosts] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [buildingStyle, setBuildingStyle] = useState<"lego" | "minecraft">("lego");

  // Use custom hook for BOM calculation
  const bomData = useBOMCalculation(layers, buildingStyle);

  const handleExport = () => {
    onExport(bomData);
  };

  const handleExportCsv = () => {
    const inventory: BomCsvRow[] = bomData.brickInventory.map((item) => ({
      id: item.brickType.id,
      name: item.brickType.name,
      colorName: item.colorName,
      quantity: item.quantity,
      unitPrice: item.estimatedPrice,
    }));
    exportBomCsv(inventory);
  };

  const handleExportBrickLinkCsv = () => {
    const inventory: BomCsvRow[] = bomData.brickInventory.map((item) => ({
      id: item.brickType.id,
      name: item.brickType.name,
      colorName: item.colorName,
      quantity: item.quantity,
      unitPrice: item.estimatedPrice,
    }));
    exportBomBrickLinkCsv(inventory);
  };

  const handleExportRebrickableCsv = () => {
    const inventory: BomCsvRow[] = bomData.brickInventory.map((item) => ({
      id: item.brickType.id,
      name: item.brickType.name,
      colorName: item.colorName,
      quantity: item.quantity,
      unitPrice: item.estimatedPrice,
    }));
    exportBomRebrickableCsv(inventory);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  return (
    <div className="bom-generator">
      <div className="bom-header">
        <h3>{t("bom.title")}</h3>
        <BOMControls
          currency={currency}
          onCurrencyChange={setCurrency}
          showCosts={showCosts}
          onShowCostsChange={setShowCosts}
          buildingStyle={buildingStyle}
          onBuildingStyleChange={setBuildingStyle}
          onExport={handleExport}
          onExportCsv={handleExportCsv}
          onExportBrickLinkCsv={handleExportBrickLinkCsv}
          onExportRebrickableCsv={handleExportRebrickableCsv}
        />
      </div>

      <BOMSummary
        bomData={bomData}
        showCosts={showCosts}
        formatCurrency={formatCurrency}
        buildingStyle={buildingStyle}
      />

      {/* The itemised table is long — it stays behind a disclosure so the
          page does not end in a wall of rows */}
      <button
        type="button"
        onClick={() => setShowDetails((prev) => !prev)}
        aria-expanded={showDetails}
        className="mt-4 flex items-center gap-2 rounded-full border-[1.5px] border-ink bg-surface px-3 py-1.5 text-sm font-semibold text-ink shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none hover:text-brick hover:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick active:translate-x-0.5 active:translate-y-0.5 active:shadow-hard-press"
      >
        {showDetails ? t("bom.hideDetails") : t("bom.showDetails")}
      </button>

      {showDetails && (
        <BOMDetails
          brickInventory={bomData.brickInventory}
          showCosts={showCosts}
          formatCurrency={formatCurrency}
        />
      )}

      <BOMNotes />
    </div>
  );
};


export default React.memo(BOMGenerator);
